import { Alert, Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import type { CryptoCurrency } from '../../queries/cryptoDashboard';
import type { StyleObj } from '../../@types';

const styles: StyleObj = {
  panel: { p: 2 },
  details: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) 2fr' },
    gap: 1,
    m: 0,
  },
  value: { m: 0, overflowWrap: 'anywhere' },
};

const displayFreshness = (stale: boolean | undefined) => {
  if (stale === undefined) return 'Not available';
  return stale ? 'Stale' : 'Current';
};

const displayValue = (value: string | number | null | undefined) => value ?? 'Not available';

const CryptoCoinInfo = ({
  currency,
  isRefreshing,
  isError,
  onRefresh,
  onClose,
}: {
  currency: CryptoCurrency;
  isRefreshing: boolean;
  isError: boolean;
  onRefresh: () => void;
  onClose: () => void;
}) => {
  const details = [
    { label: 'Catalog checked', value: currency.providerFetchedAt },
    { label: 'Catalog expires', value: currency.providerExpiresAt },
    { label: 'Catalog freshness', value: displayFreshness(currency.catalogStale) },
    { label: 'Provider active', value: currency.providerActive ? 'Yes' : 'No' },
    { label: 'Required destination tag', value: currency.tagRequired ? 'Yes' : 'No' },
    { label: 'Deposit restrictions', value: currency.depositReasons?.join(', ') ?? 'Not available' },
    { label: 'Withdrawal restrictions', value: currency.withdrawalReasons?.join(', ') ?? 'Not available' },
    { label: 'Minimum deposit', value: currency.minimumDeposit },
    { label: 'Maximum payment amount', value: currency.maximumDeposit },
    { label: 'Minimum withdrawal', value: currency.minimumWithdrawal },
    { label: 'Network fee', value: currency.networkFee },
    { label: 'Processing fee (provider value)', value: currency.processingFee },
    { label: 'Waiting time (minutes, payment forms)', value: currency.waitingTimeMinutes },
    { label: 'Token contract address', value: currency.contractAddress },
  ];

  return (
    <Paper variant='outlined' sx={styles.panel} component='section' aria-label={`Coin info: ${currency.currency}`}>
      <Stack spacing={2}>
        <Typography variant='h6'>Coin info: {currency.currency}</Typography>
        <Typography variant='body2'>
          Provider limits and fees are read-only and may change with network conditions. Values are shown as reported by
          0xProcessing, without conversion. Refresh requests fresh provider data. Normal table reads can show a previous
          snapshot while it refreshes in the background.
        </Typography>
        {currency.catalogStale && (
          <Alert severity='warning'>
            Provider catalog is stale. You can restrict existing settings; enabling requires a fresh catalog.
          </Alert>
        )}
        {isError && <Alert severity='error'>Could not refresh coin info. The previous values are still shown.</Alert>}
        <Box component='dl' sx={styles.details}>
          {details.map(({ label, value }) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography component='dt'>{label}</Typography>
              <Typography component='dd' sx={styles.value}>
                {displayValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Alert severity='info'>
          Static-wallet deposits below the minimum remain in the wallet until additional deposits reach the minimum.
          Waiting time applies to payment forms; it is not a static-wallet confirmation estimate.
        </Alert>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Button onClick={onRefresh} disabled={isRefreshing}>
            Refresh coin info
          </Button>
          <Button onClick={onClose}>Close coin info</Button>
          <Link
            href='https://docs.0xprocessing.com/0xprocessing-api-v1/informational-commands/coin-info'
            target='_blank'
            rel='noopener noreferrer'
          >
            0xProcessing Coin Info documentation
          </Link>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CryptoCoinInfo;
